import { useLocation, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { ROUTE_DESCRIPTIONS } from "@/core/router/navConfig";

/**
 * Builds a human-readable label for a single breadcrumb segment.
 *
 * Strategy: try progressively longer path prefixes against ROUTE_DESCRIPTIONS
 * to find a matching label. Fall back to title-casing the raw segment.
 */
function labelFor(segment: string, fullPath: string): string {
  // Check if the full accumulated path has a description — use its first word(s)
  const desc = ROUTE_DESCRIPTIONS[fullPath];
  if (desc) {
    // Pull the label from NAV_SECTIONS-style short name.
    // Title-case the raw segment as a cleaner fallback than the full description.
    return titleCase(segment);
  }
  return titleCase(segment);
}

function titleCase(str: string): string {
  return str
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Auto-derived breadcrumb trail based on the current route path.
 *
 * - First item is always "Home" → `/`
 * - Intermediate segments are clickable `<Link>`s
 * - Last segment is plain text (current page)
 * - Skips rendering when on the home page itself (only 1 segment)
 */
export function Breadcrumb() {
  const { pathname } = useLocation();

  const segments = pathname.split("/").filter(Boolean);

  // Don't render on the home page
  if (segments.length === 0) return null;

  // Build crumb entries: [label, href]
  const crumbs: Array<{ label: string; href: string }> = [
    { label: "Home", href: "/" },
  ];

  let accumulated = "";
  for (const segment of segments) {
    accumulated += `/${segment}`;
    crumbs.push({
      label: labelFor(segment, accumulated),
      href: accumulated,
    });
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs">
      {crumbs.map((crumb, idx) => {
        const isLast = idx === crumbs.length - 1;

        return (
          <span key={crumb.href} className="flex items-center gap-1">
            {idx > 0 && (
              <ChevronRight
                size={14}
                className="text-slate-600 flex-shrink-0"
                aria-hidden
              />
            )}
            {isLast ? (
              <span className="text-slate-200" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
